'use client';

import dynamic from 'next/dynamic';
import type { ReactNode } from 'react';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { DevDashContextValue, DevDashState, DevDashTab, ThemeMode } from '../types';
import { checkUrlForDevDashEnable, disableDevDash, getCurrentEnvironment, isDevDashEnabled } from '../utils/gating';

// Dynamic import - DevDash code is only downloaded when a user opts in via ?devdash=true
// This is critical for performance: ~50KB+ of code won't be downloaded by regular users
const DevDashOverlay = dynamic(() => import('./DevDashOverlay').then((mod) => mod.DevDashOverlay), {
  ssr: false,
});

const ACTIVATION_ALERT_DELAY_MS = 100;

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const DevDashContext = createContext<DevDashContextValue | null>(null);

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY_THEME_MODE = 'devdash.themeMode';

const DEFAULT_STATE: DevDashState = {
  activeTab: 'status',
  isOpen: false,
  themeMode: 'dark',
};

/**
 * Load theme mode from localStorage
 */
function loadThemeMode(): ThemeMode {
  if (typeof window === 'undefined') {
    return 'dark';
  }
  try {
    const saved = localStorage.getItem(STORAGE_KEY_THEME_MODE);
    return saved === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

/**
 * Save theme mode to localStorage
 */
function saveThemeMode(mode: ThemeMode): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY_THEME_MODE, mode);
  } catch {
    // Ignore storage errors
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

interface DevDashProviderProps {
  children?: ReactNode;
}

export const DevDashProvider = ({ children }: DevDashProviderProps) => {
  // Check gating on mount - if not enabled, render children only
  const [isEnabled, setIsEnabled] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  // Session-only state (resets on page reload)
  const [state, setState] = useState<DevDashState>(DEFAULT_STATE);

  // Get environment for display
  const environment = getCurrentEnvironment();

  // Check if DevDash should be enabled and load persisted settings (client-side only)
  useEffect(() => {
    setHasMounted(true);

    // First, check URL for ?devdash=true parameter and set localStorage if found
    // This must happen before checking isDevDashEnabled()
    // Returns true if activation just happened via URL
    const justActivated = checkUrlForDevDashEnable();

    // Now check if enabled (will return true if URL just enabled it)
    const enabled = isDevDashEnabled();
    setIsEnabled(enabled);

    // If not enabled, don't bother loading settings
    if (!enabled) {
      return;
    }

    // Show native browser alert if user just activated via URL
    if (justActivated) {
      // Use setTimeout to ensure it runs after React render
      setTimeout(() => {
        // biome-ignore lint/suspicious/noAlert: Intentional alert for DevDash activation confirmation
        alert(
          'Congrats! 🎉 DevDash is now activated.\n\nPress Ctrl + . to open the developer dashboard.\n\nThis access will persist until you logout from DevDash.',
        );
      }, ACTIVATION_ALERT_DELAY_MS);
    }

    // Load persisted settings from localStorage (only if enabled)
    const savedThemeMode = loadThemeMode();

    setState((prev) => ({
      ...prev,
      themeMode: savedThemeMode,
    }));
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // State Actions
  // ───────────────────────────────────────────────────────────────────────────

  const setIsOpen = useCallback((isOpen: boolean) => {
    setState((prev) => ({ ...prev, isOpen }));
  }, []);

  const toggleOpen = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  const setActiveTab = useCallback((activeTab: DevDashTab) => {
    setState((prev) => ({ ...prev, activeTab }));
  }, []);

  const setThemeMode = useCallback((themeMode: ThemeMode) => {
    setState((prev) => ({ ...prev, themeMode }));
    saveThemeMode(themeMode);
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // Reset
  // ───────────────────────────────────────────────────────────────────────────

  const resetToDefaults = useCallback(() => {
    // Reset all session state to defaults
    setState(DEFAULT_STATE);

    // Clear persisted settings
    saveThemeMode('dark');
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // Logout - Disable DevDash access
  // ───────────────────────────────────────────────────────────────────────────

  const logout = useCallback(() => {
    // Remove the enabled flag from localStorage
    disableDevDash();
    // Close the panel
    setState((prev) => ({ ...prev, isOpen: false }));
    // Disable DevDash
    setIsEnabled(false);
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // Keyboard Shortcut
  // ───────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+. (period) to toggle - works on all platforms, no browser conflicts
      if (event.ctrlKey && event.key === '.') {
        event.preventDefault();
        toggleOpen();
      }

      // Escape to close
      if (event.key === 'Escape' && state.isOpen) {
        event.preventDefault();
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEnabled, state.isOpen, toggleOpen, setIsOpen]);

  // ───────────────────────────────────────────────────────────────────────────
  // Context Value
  // ───────────────────────────────────────────────────────────────────────────

  const contextValue = useMemo<DevDashContextValue>(
    () => ({
      environment,
      isEnabled,
      logout,
      resetToDefaults,
      setActiveTab,
      setIsOpen,
      setThemeMode,
      state,
      toggleOpen,
    }),
    [state, isEnabled, environment, setIsOpen, toggleOpen, setActiveTab, setThemeMode, resetToDefaults, logout],
  );

  // ───────────────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────────────
  //
  // CRITICAL: DevDash must have ZERO impact on the component tree when not open.
  // We achieve this by:
  //   1. ALWAYS returning children directly (never wrapping in Context.Provider)
  //   2. Rendering DevDash overlay via React Portal to document.body
  //
  // This ensures that enabling DevDash (setting localStorage) has absolutely
  // no effect on hydration, component structure, or any other React behavior
  // until the user actually opens the DevDash panel.
  // ───────────────────────────────────────────────────────────────────────────

  // Render DevDash overlay via portal (completely outside the component tree)
  const devDashPortal =
    hasMounted && isEnabled
      ? createPortal(
          <DevDashContext.Provider value={contextValue}>
            <DevDashOverlay />
          </DevDashContext.Provider>,
          document.body,
        )
      : null;

  // Children are ALWAYS passed through unchanged - DevDash never affects the component tree
  return (
    <>
      {children}
      {devDashPortal}
    </>
  );
};

// biome-ignore lint/style/useComponentExportOnlyModules: Context needed for useDevDash hook
export { DevDashContext };
